import { Component, OnDestroy } from '@angular/core';
import { ConfirmationService, Message, MessageService } from 'primeng/api';
import { Subscription, catchError, tap } from 'rxjs';

import { Cargo } from '@app/core/models/questionario-cargo';
import { Concurso } from '@app/core/models/concurso';
import { FiltroQuestionarioCargo } from '../filtro-questionario-cargo';
import { QuestionarioCargoApiService } from '@app/tabelasAdministrativas/services/questionario-cargo-api.service';
import { ResultadoQuestionarioCargo } from '@app/core/models/resultado-questionario-cargo';
import { UiService } from '@app/core/services/ui.service';

@Component({
	templateUrl: './tabela-questionario-cargo.component.html',
	providers: [MessageService, ConfirmationService]
})
export class TabelaQuestionarioCargoComponent implements OnDestroy {
	habilitarFiltroCadastradoUsuario = false;
	messages: Message[] = [];
	resultado?: ResultadoQuestionarioCargo;
	filtro?: FiltroQuestionarioCargo;
	itensPorPagina = 20;
	loadingResultado = false;
	limparFormularioPesquisa = false;
	totalResultado?: number;
	operacao?: string;
	subscriptions: Subscription[] = [];
	pagina?: number;
	mensagensErro: any[] = [];
	exibirModalManterTabela = false;
	concurso?: Concurso;
	itemTabela? = new Cargo();
	tituloModalManterTabela = '';
	registrosSelecionados: Cargo[] = [];

	constructor(
		private confirmationService: ConfirmationService,
		private cargoApiService: QuestionarioCargoApiService,
		private messageService: MessageService,
		private ui: UiService
	) {}


	ngOnDestroy(): void {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
	}
	
	executarPesquisa(event: FiltroQuestionarioCargo): void {
		this.filtro = event;
		this.pagina = 1;
		this.messageService.clear();
		this.pesquisar();
	}
	
	pesquisar() {
		this.resultado = undefined;
		this.loadingResultado = true;
		this.limparMensagensErro();
		if (this.filtro) {
			this.filtro.page = this.pagina!;
			this.filtro.size = this.itensPorPagina;
			this.filtro.calcularFirst();

			this.subscriptions.push(
				this.cargoApiService
				.pesquisarCargos(this.filtro)
				.pipe(tap((res) => {
						this.resultado = res;
						this.loadingResultado = false;
						this.totalResultado = this.resultado.metadata!.total;
						this.pagina = this.resultado.metadata!.page;
						this.resultado.items?.sort((a, b) => (a?.nomeCargo ?? "").localeCompare((b?.nomeCargo ?? "")));		
					}),
					catchError(err => {
						this.totalResultado = 0;
						this.resultado = undefined;
						this.criarMensagem('error', err.error.message);
						throw err;
					})

				)
				.subscribe()
			);
		}
	}

	prepararCadastrar() {
		this.operacao = 'cadastrar';
		this.tituloModalManterTabela = 'Cadastrar cargo';
		this.itemTabela = new Cargo();
		this.exibirModalManterTabela = true;
	}

	prepararAlterar(event: any) {
		this.limparMensagensErro();
		this.tituloModalManterTabela = 'Alterar cargo';
		this.operacao = 'alterar';

		if (!this.concurso || (this.concurso.id != event.idConcurso) ) {
			this.subscriptions.push(
				this.cargoApiService.obterConcurso(event.idConcurso).subscribe(res => {
					this.concurso = new Concurso(
						res.id,
						res.nome,
						res.utiliza_perfil,
						res.codigo,
						res.situacao
					);
					this.obterCargoAlterar(this.concurso!.codigo!, event.codigoOrgao, event.codigoCargo);
				})
			);
			return;
		}
		this.obterCargoAlterar(this.concurso!.codigo!, event.codigoOrgao, event.codigoCargo);
	}

	obterCargoAlterar(concursoCodigo: string, codigoOrgao: any, codigoCargo: any) {
		this.subscriptions.push(
			this.cargoApiService.obterCargo(concursoCodigo, codigoOrgao, codigoCargo).subscribe(res => {
				this.itemTabela = new Cargo(
					res.id,
					res.nomeCargo,
					res.codigoCargo,
					res.idQuestionario,
					this.concurso!.id, 	
					res.codigoOrgao,
					res.dataInicioLiberacao,
					res.dataFimLiberacao,
					res.situacao
				);
				this.exibirModalManterTabela = true;
			})
		);
	}

	salvarItem(event: Cargo) {
		const cargo = new Cargo();
		cargo.id = event.id;
		cargo.nomeCargo = event.nomeCargo;
		cargo.codigoCargo = event.codigoCargo;
		cargo.codigoOrgao = event.codigoOrgao;
		cargo.idConcurso = event.idConcurso;
	
		if (this.operacao === 'cadastrar') {
			this.salvarNovo(cargo);
		} else if (cargo.id) {
			this.alterar(cargo.id, cargo);
		}
	}


	excluirItem(event: any) {
		this.limparMensagensErro();
		const cargo = event;
		this.confirmationService.confirm({
			message: 'Esta ação não poderá ser desfeita.',
			header: 'Deseja excluir o cargo ' + cargo.nomeCargo + '?',
			accept: async () => {
				this.subscriptions.push(
					this.cargoApiService.excluirCargo(event.idCargo).subscribe({
						next: async (res: any) => {
							this.exibirMensagemSucesso(res);
						},
						error: err => {
							this.criarMensagem('error', err.error.message);
						}
					})
				);
			}
		});
	}


	paginar(event: any) {
		this.pagina = event.first / event.rows + 1;
		this.itensPorPagina = event.rows;
		this.pesquisar();
	}

	getItensPorPagina(event: any) {
		this.itensPorPagina = event.rows;
	}

	getConcurso(event: Concurso) {
		this.concurso = event;
	}

	limparResultado() {
		this.resultado = undefined;
	}

	private salvarNovo(cargo: Cargo) {
		this.subscriptions.push(
			this.cargoApiService.incluirCargo(cargo).subscribe(
				(res: any) => {
					this.exibirMensagemSucesso(res);
					this.exibirModalManterTabela = false;
					this.mensagensErro = [];
				},
				err => {
					this.exibirModalManterTabela = true;
					this.mensagensErro = [{ severity: 'error', summary: '', detail: err.error.message }];
				}
			)
		);
	}

	private alterar(id: number, cargo: Cargo) {
		this.subscriptions.push(this.cargoApiService
				.alterarCargo(id, cargo)
				.pipe(
					tap((res: any) => {
						this.exibirModalManterTabela = false;
						this.mensagensErro = [];
						this.exibirMensagemSucesso(res);
					}),
					catchError(err => {
						this.exibirModalManterTabela = true;
						this.mensagensErro = [{ severity: 'error', summary: '', detail: err.error.message }];
						return [this.mensagensErro];
					})
				).subscribe()			
			);

	}

	private criarMensagem(criticidade: string, mensagem: string) {
		this.messageService.clear();
		this.messageService.add(this.ui.criarMensagem(criticidade, '', mensagem));
	}

	private exibirMensagemSucesso(res: any) {
		if (res) {
			this.criarMensagem('success', res.message);
			this.limparPesquisa();
		}
	}

	private limparMensagensErro() {
		this.mensagensErro = [];
	}

	private limparPesquisa() {
		this.limparFormularioPesquisa = true;
		this.pagina = 1;
		this.pesquisar();
	}
}
